import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CryptoJs from "crypto-js";
import { button, label } from "../../config/constants";
import { sendOtpForPasswordReset, verifyOtpForPasswordReset, resetPasswordAfterOtp } from "../../shared/services/auth.service";
import SelectRecoveryMethod from "./SelectRecoveryMethod";
import OtpVerification from "./OtpVerification";
import SetNewPassword from "./SetNewPassword";
import "./InAppForgotPassword.scss";
import { STEP } from "./OtpConfig";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Button } from "@progress/kendo-react-buttons";



/**
 * Orchestrator modal for the in-app forgot password flow.
 * Manages step state between SelectRecoveryMethod and OtpVerification screens.
 * @author Sarthak Arora
 */
const InAppForgotPassword = ({ show, onClose, onBack }) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);

  const email = auth?.user?.email || "";
  const mobile = auth?.user?.MobileNo || "";

  const getInitialSelectedMethods = () => {
    const methods = [];
    if (email) methods.push("email");
    if (mobile) methods.push("mobile");
    if (methods.length === 1) return methods;
    return methods;
  };

  const [step, setStep] = useState(STEP.SELECT_METHOD);
  const [selectedMethods, setSelectedMethods] = useState(getInitialSelectedMethods);
  const [currentOtp, setCurrentOtp] = useState("");
  const [otpVerifiedToken, setOtpVerifiedToken] = useState("");
  const [newPasswordData, setNewPasswordData] = useState({ newPassword: "", isValid: false });

  useEffect(() => {
    if (show) {
      setStep(STEP.SELECT_METHOD);
      setSelectedMethods(getInitialSelectedMethods());
      setCurrentOtp("");
      setOtpVerifiedToken("");
      setNewPasswordData({ newPassword: "", isValid: false });
    }
  }, [show]);

  const isOtpComplete = currentOtp.length === 4;
  const isSendOtpEnabled = selectedMethods.length > 0;

  /**
   * Handles the send OTP event.
   * @author Sarthak Arora
   */
  const handleSendOtp = async () => {
    const result = await dispatch(
      sendOtpForPasswordReset({ methods: selectedMethods, email, mobile })
    );
    if (result?.success) {
      setCurrentOtp("");
      setStep(STEP.VERIFY_OTP);
    }
  };

  /**
   * Handles the verify OTP event.
   * @author Sarthak Arora
   */
  const handleVerifyOtp = async () => {
    if (!isOtpComplete) return;
    const result = await dispatch(verifyOtpForPasswordReset({ otp: currentOtp }));
    if (result?.success) {
      setOtpVerifiedToken(result.otpVerifiedToken || "");
      setNewPasswordData({ newPassword: "", isValid: false });
      setStep(STEP.SET_NEW_PASSWORD);
    }
  };

  /**
   * Handles the save new password event.
   * @author Sarthak Arora
   */
  const handleSaveNewPassword = async () => {
    if (!newPasswordData.isValid) return;
    const hashedPassword = CryptoJs.SHA1(newPasswordData.newPassword, email).toString();
    const result = await dispatch(resetPasswordAfterOtp({ token: otpVerifiedToken, password: hashedPassword }));
    if (result?.success) {
      onClose();
    }
  };

  /**
   * Handles the back from OTP event.
   * @author Sarthak Arora
   */
  const handleBackFromOtp = () => {
    setStep(STEP.SELECT_METHOD);
    setCurrentOtp("");
  };

  /**
   * Handles the back from set new password event.
   * @author Sarthak Arora
   */
  const handleBackFromSetNewPassword = () => {
    setStep(STEP.VERIFY_OTP);
    setCurrentOtp("");
  };

  /**
   * Builds the modal content based on the current step.
   * @author Sarthak Arora
   */
  const buildModalContent = () => {
    if (step === STEP.SELECT_METHOD) {
      return (
        <SelectRecoveryMethod
          email={email}
          mobile={mobile}
          selectedMethods={selectedMethods}
          onMethodChange={setSelectedMethods}
        />
      );
    }
    if (step === STEP.VERIFY_OTP) {
      return (
        <OtpVerification
          selectedMethods={selectedMethods}
          email={email}
          mobile={mobile}
          onOtpChange={setCurrentOtp}
        />
      );
    }
    return (
      <SetNewPassword onPasswordChange={setNewPasswordData} />
    );
  };

  /**
   * Builds the buttons based on the current step.
   * @author Sarthak Arora
   */
  const buildButtons = () => {
    if (step === STEP.SELECT_METHOD) {
      return [
        { label: button.BACK, action: "back", disabled: false },
        { label: label.SEND_OTP, action: "sendOtp", disabled: !isSendOtpEnabled },
      ];
    }
    if (step === STEP.VERIFY_OTP) {
      return [
        { label: button.BACK, action: "back", disabled: false },
        { label: label.VERIFY_AND_PROCEED, action: "verifyOtp", disabled: !isOtpComplete },
      ];
    }
    return [
      { label: button.BACK, action: "back", disabled: false },
      { label: label.SAVE, action: "savePassword", disabled: !newPasswordData.isValid },
    ];
  };

  /**
   * Handles the button click event.
   * @author Sarthak Arora
   */
  const handleButtonClick = (actionType) => {
    switch (actionType) {
      case "back":
        if (step === STEP.SELECT_METHOD) {
          onBack();
        } else if (step === STEP.VERIFY_OTP) {
          handleBackFromOtp();
        }
        else if (step === STEP.SET_NEW_PASSWORD) {
          handleBackFromSetNewPassword();
        }
        break;
      case "sendOtp":
        handleSendOtp();
        break;
      case "verifyOtp":
        handleVerifyOtp();
        break;
      case "savePassword":
        handleSaveNewPassword();
        break;
      default:
        break;
    }
  };

  if (!show) return null;

  return (
    <InAppForgotPasswordModal
      show={show}
      heading={label.RESET_PASSWORD_MODAL_TITLE}
      content={buildModalContent}
      buttons={buildButtons()}
      onButtonClick={handleButtonClick}
      onClose={onClose}
    />
  );
};

/**
 * uses the same Dialog from KendoReact but with
 * flexible multi-action buttons 
 * @author Sarthak Arora
 */
const InAppForgotPasswordModal = ({ show, heading, content, buttons, onButtonClick, onClose }) => {

  return (
    <Dialog onClose={onClose} visible={show} className='custom-dialog-wrapper in-app-forgot-password-modal'>
      <div className='dt-popup p-2'>
        <div className='dt-popup-header d-flex justify-content-between align-items-center pt-2 pb-2 pl-3 pr-3'>
          <div className='dt-popup-title'>{heading}</div>
          <span className='dt-popup-cancel-btn cursor-pointer ml-2' onClick={onClose} title='Close'>
            <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
              &#x2715;
            </Tooltip>
          </span>
        </div>
        <div className='dt-popup-body'>
          <p>{content()}</p>
        </div>
        <div className='dt-popup-footer text-right'>
          {buttons.map((btn, i) => (
            <Button
              key={i}
              className={`btn-width btn ${btn.action === "back" ? "btn-secondary mr-2" : "btn-primary"}`}
              disabled={btn.disabled}
              onClick={() => onButtonClick(btn.action)}
            >
              {btn.label}
            </Button>
          ))}
        </div>
      </div>
    </Dialog>
  );
}

export default InAppForgotPassword;

